import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X, Heart } from "lucide-react";
import LogoText from "./LogoText";
import ApologeticsScriptureBanner from "./ApologeticsScriptureBanner";

/* Top nav — TIGHT. Six items, that's it. Everything else lives in the
   footer or the dashboard side panel. Mike's complaint: "two things named
   too close" (Funnels in nav vs Funnelrama as page title) and overcrowded
   right side pushing SIGN IN off-screen. Both fixed below. */
const TOP_ITEMS = [
  { label: "The Book", to: "/with-faith",  testid: "nav-book" },
  { label: "Market",   to: "/market",       testid: "nav-market" },
  { label: "What You Earn", to: "/what-you-earn", testid: "nav-what-you-earn" },
  { label: "Library",  to: "/library",      testid: "nav-library" },
  { label: "Mirror Site",  to: "/dashboard/my-mirrors",   testid: "nav-mirror-site" },
  { label: "Academy", to: (u) => (u ? "/academy" : "/academy"), testid: "nav-academy" },
  { label: "Our Mission", to: "/founder",      testid: "nav-mission" },
  { label: "Pricing",  to: "/pricing",      testid: "nav-pricing" },
];

export default function SiteNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const linkCls = "font-overline uppercase tracking-[0.14em] text-[12px] text-foreground hover:text-red-wine transition-colors";

  return (
    <header data-testid="site-nav" className="sticky top-0 z-50 w-full backdrop-blur-xl bg-background/85 border-b border-border/60">
      <ApologeticsScriptureBanner />
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-3 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <LogoText size="md" as="span" />
        </Link>

        {/* Desktop nav — visible at LG+ (1024px+). Only 6 items + AI Writer
            so it fits cleanly without crowding SIGN IN. */}
        <nav className="hidden lg:flex items-center gap-5 flex-1 justify-center" data-testid="desktop-nav">
          {TOP_ITEMS.map(item => (
            <Link key={item.label} to={typeof item.to === "function" ? item.to(user) : item.to} data-testid={item.testid} className={linkCls}>
              {item.label}
            </Link>
          ))}
          <Link to={user ? "/dashboard/ai-writer" : "/signup?plan=free"} data-testid="nav-ai-writer" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider-cap inline-flex items-center gap-1.5 shadow transition border border-[#F39A1F] whitespace-nowrap">
            <span className="text-[#F39A1F]">★</span> AI Writer
          </Link>
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <>
              <Link data-testid="nav-dashboard" to="/dashboard" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-bold uppercase tracking-wider-cap shadow transition whitespace-nowrap">My Office</Link>
              <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="btn-outline-gold !py-2 !px-3 !text-xs hidden md:inline-flex">Sign out</button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="bg-white border-2 border-[#0F172A] text-[#0F172A] px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-bold uppercase tracking-wider-cap hover:bg-[#0F172A] hover:text-white shadow transition whitespace-nowrap">Sign In</Link>
              <Link data-testid="nav-getstarted" to="/signup?plan=free" className="bg-[#F39A1F] text-[#0F172A] hover:bg-[#F39A1F]/90 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-bold uppercase tracking-wider-cap shadow whitespace-nowrap">★ Get Started</Link>
            </>
          )}
          {/* Mobile/tablet hamburger — visible below 1024px (lg) */}
          <button onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" className="lg:hidden p-2 rounded-md text-foreground hover:bg-white" aria-label="Toggle menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-border/60 bg-background">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {TOP_ITEMS.map(item => (
              <Link key={item.label} to={typeof item.to === "function" ? item.to(user) : item.to} onClick={() => setOpen(false)}
                    data-testid={`mnav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-3 py-3 rounded-md font-overline uppercase tracking-[0.14em] text-sm text-foreground hover:bg-white hover:text-red-wine">
                {item.label}
              </Link>
            ))}
            <Link to={user ? "/dashboard/ai-writer" : "/signup?plan=free"} onClick={() => setOpen(false)} data-testid="mnav-ai-writer" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-3 py-3 rounded-md font-bold uppercase tracking-wider-cap text-sm inline-flex items-center gap-1.5 shadow border border-[#F39A1F]">
              <span className="text-[#F39A1F]">★</span> AI Writer
            </Link>
            <div className="border-t border-border/60 mt-2 pt-3 flex gap-2 flex-wrap">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-primary flex-1 text-center !py-2.5 !text-xs">My Office</Link>
                  <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="btn-outline-gold !py-2 !px-4 !text-xs">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-gold !py-2 !px-4 !text-xs flex-1 text-center">Sign in</Link>
                  <Link to="/signup?plan=free" onClick={() => setOpen(false)} className="btn-primary !py-2.5 !px-4 !text-xs flex-1 text-center">★ Get Started</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
